import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserSession } from '../contexts/UserContext.js';
import { usePendingOperations } from '../contexts/PendingOperationsContext.js';
import { isDevelopment } from '../utils/environment.js';
import { getFlagFromCountryName } from '../utils/countryUtils.js';
import FomoAlert from './FomoAlert.js';
import ImageCropModal from './ImageCropModal.js';
import { ProfileEmailVerification } from './ProfileVerificationModal.js';
import './Profile.css';
import back from '../Ui/backWhite.png';
import editIcon from '../Ui/pencil.png';

const Profile = ({ goBack }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { userSession, loadUserSession } = useUserSession();
  const { trackOperation, requestNavigation } = usePendingOperations();
  
  // Development bypass pattern for testing
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const allowDevBypass = isLocalhost && isDevelopment();
  
  // Check if user has access (paid and session loaded) OR development bypass
  const hasAccess = (userSession.isLoaded && userSession.hasPaid) || allowDevBypass;
  
  // FOMO alert state
  const [showFomoAlert, setShowFomoAlert] = useState(false);
  
  // Image crop modal state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  
  // Email verification modal state
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  // Individual field editing states
  const [editingField, setEditingField] = useState(null); // 'givenName', 'familyName', 'email', or null
  
  const [formData, setFormData] = useState({
    givenName: '',
    familyName: '',
    email: '',
    profilePicture: null
  });

  // Initialize form data from database data in userSession (NO Auth0 fallback)
  useEffect(() => {
    if (userSession.isLoaded && userSession.user) {
      setFormData({
        givenName: userSession.user.givenName || '',
        familyName: userSession.user.familyName || '',
        email: userSession.user.email || userSession.email || '',
        profilePicture: userSession.user.picture || null
      });
    }
  }, [userSession.isLoaded, userSession.user?.givenName, userSession.user?.familyName, userSession.user?.email, userSession.user?.picture, userSession.email]);

  // Wrap goBack with navigation guard - blocks if save in progress
  const handleGoBack = useCallback(() => {
    requestNavigation(() => goBack());
  }, [goBack, requestNavigation]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    // Store the file and show crop modal
    setPendingImageFile(file);
    
    // Create object URL for the crop modal
    const imageUrl = URL.createObjectURL(file);
    setCropImageSrc(imageUrl);
    setShowCropModal(true);
  };

  const handleCropComplete = async (croppedImageBlob, croppedImagePreview) => {
    setShowCropModal(false);
    setIsUploadingImage(true);

    // Track this operation so navigation is blocked until complete
    await trackOperation('upload-profile-picture', async () => {
      try {
        const token = await getAccessTokenSilently();
        const apiUrl = process.env.REACT_APP_API_URL || 'https://intellegacy-back.vercel.app';
        const cleanApiUrl = apiUrl.replace(/\/$/, '');
        
        const formDataUpload = new FormData();
        // Create a file from the blob with the original filename
        const fileName = pendingImageFile?.name || 'profile-picture.jpg';
        const croppedFile = new File([croppedImageBlob], fileName, { type: 'image/jpeg' });
        formDataUpload.append('profilePicture', croppedFile);

        // Use proper user picture upload endpoint
        const response = await fetch(`${cleanApiUrl}/api/user/picture`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataUpload
        });

        if (response.ok) {
          const result = await response.json();
          setFormData(prev => ({
            ...prev,
            profilePicture: result.pictureUrl || result.picture
          }));
          // Refresh user session to get updated data from database
          await loadUserSession(true);
          alert('Profile picture uploaded successfully!');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to upload image');
        }
      } catch (error) {
        alert(`Failed to upload profile picture: ${error.message}`);
      } finally {
        setIsUploadingImage(false);
        setPendingImageFile(null);
        setCropImageSrc(null);
        // Clean up the object URL
        if (cropImageSrc) {
          URL.revokeObjectURL(cropImageSrc);
        }
      }
    });
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setPendingImageFile(null);
    // Clean up the object URL
    if (cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
      setCropImageSrc(null);
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    
    // Check if email has changed
    const currentEmail = userSession.user?.email || userSession.email || '';
    const newEmail = formData.email;
    
    if (newEmail !== currentEmail && newEmail.trim() !== '') {
      // Email has changed, require verification
      setOriginalEmail(currentEmail);
      setPendingEmail(newEmail);
      setShowEmailVerification(true);
      return;
    }
    
    // Proceed with normal save (no email change)
    await performSave();
  };

  // Validation helpers matching backend schema
  const validateName = (name) => {
    if (!name) return true; // Allow empty
    if (name.length > 100) return false;
    return /^[a-zA-Z\s\-'\.]+$/.test(name);
  };

  const validateEmail = (email) => {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailVerified = async (verifiedEmail) => {
    // Update form data with verified email
    setFormData(prev => ({ ...prev, email: verifiedEmail }));
    
    // Close the verification modal
    setShowEmailVerification(false);
    
    // Proceed with save
    await performSave();
    setEditingField(null); // Close editing mode after successful save
  };

  const handleEmailSave = async () => {
    if (isSaving) return;
    
    // Check if email has changed
    const currentEmail = userSession.user?.email || userSession.email || '';
    const newEmail = formData.email;
    
    if (newEmail !== currentEmail && newEmail.trim() !== '') {
      // Email has changed, require verification
      setOriginalEmail(currentEmail);
      setPendingEmail(newEmail);
      setShowEmailVerification(true);
      return;
    }
    
    // If email hasn't changed, proceed with normal save
    await performSave();
    setEditingField(null);
  };

  const performSave = async () => {
    // Validate before saving
    if (!validateName(formData.givenName)) {
      alert('First name can only contain letters, spaces, hyphens, apostrophes, and dots (max 100 characters)');
      return;
    }
    if (!validateName(formData.familyName)) {
      alert('Last name can only contain letters, spaces, hyphens, apostrophes, and dots (max 100 characters)');
      return;
    }
    if (formData.email && !validateEmail(formData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    setIsSaving(true);
    
    // Track this operation so navigation is blocked until complete
    await trackOperation('save-profile', async () => {
      try {
        const token = await getAccessTokenSilently();
        const apiUrl = process.env.REACT_APP_API_URL || 'https://intellegacy-back.vercel.app';
        const cleanApiUrl = apiUrl.replace(/\/$/, '');
        
        // Use proper user profile update endpoint
        const response = await fetch(`${cleanApiUrl}/api/user/profile`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            givenName: formData.givenName,
            familyName: formData.familyName,
            email: formData.email
          })
        });

        if (response.ok) {
          // Refresh user session to get updated data from database
          await loadUserSession(true);
          setIsEditing(false);
          alert('Profile updated successfully!');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update profile');
        }
      } catch (error) {
        alert(`Failed to update profile: ${error.message}`);
      } finally {
        setIsSaving(false);
      }
    });
  };

  const handleCancel = () => {
    // Reset form data to current database values
    setFormData({
      givenName: userSession.user?.givenName || '',
      familyName: userSession.user?.familyName || '',
      email: userSession.user?.email || userSession.email || '',
      profilePicture: userSession.user?.picture || null
    });
    setIsEditing(false);
  };

  // Show loading state while session is loading
  if (!userSession.isLoaded) {
    return (
      <div className="profile-container">
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  // Check access control - only paid users can access profile
  if (!hasAccess) {
    return (
      <div className="profile-container">
        <div className="profile-header">
          <button className="back-button" onClick={handleGoBack}>
            <img src={back} className="back-btn-arrow"></img> Back to Dashboard
          </button>
          <h1>Profile Settings</h1>
        </div>
        
        {/* Show the profile interface but make it look disabled */}
        <div className="profile-content disabled-preview">
          <div className="profile-picture-section">
            <div className="profile-picture">
              <img 
                src={userSession?.user?.picture || userSession?.profilePicture || 'https://via.placeholder.com/150'} 
                alt="Profile" 
              />
              <div className="profile-picture-overlay">
                <span>🔒</span>
              </div>
            </div>
            <button 
              className="change-picture-btn disabled"
              onClick={() => setShowFomoAlert(true)}
            >
              Change Picture
            </button>
          </div>

          <div className="profile-form">
            <div className="form-group">
              <label htmlFor="givenName">First Name</label>
              <input
                type="text"
                id="givenName"
                value={userSession?.user?.givenName || ''}
                disabled
                className="disabled"
              />
            </div>

            <div className="form-group">
              <label htmlFor="familyName">Last Name</label>
              <input
                type="text"
                id="familyName"
                value={userSession?.user?.familyName || ''}
                disabled
                className="disabled"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={userSession?.user?.email || ''}
                disabled
                className="disabled"
              />
            </div>

            <div className="profile-actions">
              <button 
                className="save-button disabled"
                onClick={() => setShowFomoAlert(true)}
              >
                🔒 Save Changes (Premium)
              </button>
              <button 
                className="edit-button disabled"
                onClick={() => setShowFomoAlert(true)}
              >
                🔒 Edit Profile (Premium)
              </button>
            </div>
          </div>
        </div>

        <FomoAlert 
          isOpen={showFomoAlert}
          onClose={() => setShowFomoAlert(false)}
          featureName="Profile Management"
          customMessage="Probably want to complete registration first, then maybe you'll be allowed to edit your profile and upload pictures. Just saying... 🤷‍♂️"
        />
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
          <button className="back-button" onClick={handleGoBack}>
            <img src={back} className="back-btn-arrow"></img> Back to Dashboard
          </button>
        <h1>Profile Settings</h1>
      </div>

      <div className="profile-content">
        <div className="profile-picture-section">
          <div className="profile-picture-container">
            {formData.profilePicture ? (
              <img 
                src={formData.profilePicture} 
                alt="Profile" 
                className="profile-picture"
              />
            ) : (
              <div className="profile-picture-placeholder">
                <span>No Image</span>
              </div>
            )}
            
            <div className="picture-edit-overlay">
              <label htmlFor="profile-picture-input" className={`pencil-icon ${isUploadingImage ? 'uploading' : ''}`}>
                {isUploadingImage ? '⏳' : '✏️'}
              </label>
              <input
                id="profile-picture-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="file-input"
                disabled={isUploadingImage}
              />
            </div>
          </div>
          
          {/* Status Badges moved near profile picture */}
          <div className="profile-status-section">
            <label>Status Badges</label>
            <div className="account-status-tags">
              {/* Early Adopter */}
              {(userSession?.earlyAdopter || userSession?.user?.earlyAdopter) && (
                <span className="status-tag achievement-early-adopter">🍀 Early Adopter</span>
              )}
              
              {/* Plan Type */}
              {userSession?.planType && userSession.planType !== 'Unassigned' && (
                <span className={`status-tag plan-${userSession.planType.toLowerCase()}`}>
                  {userSession.planType}
                </span>
              )}
              
              {/* Contributor */}
              {((userSession?.user?.contributor || userSession?.contributor) || 
                (userSession?.user?.referralCount >= 2 || userSession?.referralCount >= 2)) && (
                <span className="status-tag achievement-contributor">🪐 Contributor</span>
              )}
              
              {/* Authorization Level */}
              {userSession?.authorizationGrade && userSession.authorizationGrade !== 'Unassigned' && (
                <span className={`status-tag auth-${userSession.authorizationGrade.toLowerCase()}`}>
                  {userSession.authorizationGrade === 'God' && '🛸 '}
                  {userSession.authorizationGrade === 'Immortal' && '🌱 '}
                  {userSession.authorizationGrade === 'Restricted' && '🥔 '}
                  {userSession.authorizationGrade === 'Banned' && '🚨 '}
                  {userSession.authorizationGrade === 'Suspended' && '🗿 '}
                  {userSession.authorizationGrade}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="profile-section"><br/>
          <h2 className="profile-section-title">🪂 Personal Information</h2>
          <p className="profile-section-description">
            Manage your basic profile information and identity details.
          </p>

          <div className="profile-setting-item">
            <div className="profile-setting-info">
              <h3>First Name</h3>
              <p>Your given name as it appears on official documents.</p>
              <div className="profile-setting-value">
                {editingField === 'givenName' ? (
                  <div className="inline-edit-container">
                    <input
                      type="text"
                      value={formData.givenName}
                      onChange={(e) => setFormData(prev => ({ ...prev, givenName: e.target.value }))}
                      className="inline-edit-input"
                      autoFocus
                    />
                    <div className="inline-edit-actions">
                      <button 
                        className="inline-save-btn"
                        onClick={async () => {
                          await handleSave();
                          setEditingField(null);
                        }}
                        disabled={isSaving}
                      >
                        💾
                      </button>
                      <button 
                        className="inline-cancel-btn"
                        onClick={() => {
                          handleCancel();
                          setEditingField(null);
                        }}
                      >
                        ❌
                      </button>
                    </div>
                  </div>
                ) : (
                  formData.givenName || 'Not set'
                )}
              </div>
            </div>
            <div className="profile-setting-control">
              <button 
                className="copy-link-btn"
                onClick={() => setEditingField('givenName')}
                disabled={editingField !== null}
              >
                ✏️ Edit
              </button>
            </div>
          </div>

          <div className="profile-setting-item">
            <div className="profile-setting-info">
              <h3>Last Name</h3>
              <p>Your family name or surname.</p>
              <div className="profile-setting-value">
                {editingField === 'familyName' ? (
                  <div className="inline-edit-container">
                    <input
                      type="text"
                      value={formData.familyName}
                      onChange={(e) => setFormData(prev => ({ ...prev, familyName: e.target.value }))}
                      className="inline-edit-input"
                      autoFocus
                    />
                    <div className="inline-edit-actions">
                      <button 
                        className="inline-save-btn"
                        onClick={async () => {
                          await handleSave();
                          setEditingField(null);
                        }}
                        disabled={isSaving}
                      >
                        💾
                      </button>
                      <button 
                        className="inline-cancel-btn"
                        onClick={() => {
                          handleCancel();
                          setEditingField(null);
                        }}
                      >
                        ❌
                      </button>
                    </div>
                  </div>
                ) : (
                  formData.familyName || 'Not set'
                )}
              </div>
            </div>
            <div className="profile-setting-control">
              <button 
                className="copy-link-btn"
                onClick={() => setEditingField('familyName')}
                disabled={editingField !== null}
              >
                ✏️ Edit
              </button>
            </div>
          </div>

          <div className="profile-setting-item">
            <div className="profile-setting-info">
              <h3>Email Address</h3>
              <p>Your primary email for account communications and notifications.</p>
              <div className="profile-setting-value">
                {editingField === 'email' ? (
                  <div className="inline-edit-container">
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="inline-edit-input"
                      autoFocus
                    />
                    <div className="inline-edit-actions">
                      <button 
                        className="inline-save-btn"
                        onClick={handleEmailSave}
                        disabled={isSaving}
                      >
                        💾
                      </button>
                      <button 
                        className="inline-cancel-btn"
                        onClick={() => {
                          handleCancel();
                          setEditingField(null);
                        }}
                      >
                        ❌
                      </button>
                    </div>
                  </div>
                ) : (
                  formData.email || 'Not set'
                )}
              </div>
            </div>
            <div className="profile-setting-control">
              <button 
                className="copy-link-btn"
                onClick={() => setEditingField('email')}
                disabled={editingField !== null}
              >
                ✏️ Edit
              </button>
            </div>
          </div>
        </div>

        <div className="profile-info">
          <div className="info-tags-row">
            <div className="info-tag-group">
              <label>Legacy Coins</label>
              <span className="info-tag legacy-coins-tag">
                💰 {userSession?.user?.legacyCoins || userSession?.legacyCoins || 0}
              </span>
            </div>
            
            <div className="info-tag-group">
              <label>People Referred</label>
              <span className="info-tag referral-count-tag">
                🍻 {userSession?.user?.referralCount || userSession?.referralCount || 0}
              </span>
            </div>
            
            <div className="info-tag-group">
              <label>Phone Number</label>
              <span className="info-tag phone-tag">
                📱 {userSession?.user?.phoneNumber || userSession?.phoneNumber || 'Not set'}
              </span>
            </div>
          </div>

          <div className="info-tags-row">
            <div className="info-tag-group">
              <label>Country</label>
              <span className="info-tag country-tag">
                {getFlagFromCountryName(userSession?.user?.country || userSession?.country) || '🌍'} {userSession?.user?.country || userSession?.country || 'Not set'}
              </span>
            </div>
          </div>
        </div>

                  <div className="referral-link-section">
            <label>Your Referral Link</label>
            <div className="wrapper">
              <div className="letters2">
                {userSession?.user?.referralCode ? 
                  `https://intellegacy.app/register?ref=${userSession.user.referralCode}`.split("").map((letter, index) => (
                    <span key={index} className="letter">
                      {letter}
                    </span>
                  )) : (
                    <span className="loading-message">No referral code assigned</span>
                  )
                }
              </div>
              <div className="button-wrapper-in">
                <button 
                  className="copy-link-btn"
                  onClick={() => {
                    if (userSession?.user?.referralCode) {
                      navigator.clipboard.writeText(`https://intellegacy.app/register?ref=${userSession.user.referralCode}`);
                      alert('Referral link copied to clipboard!');
                    }
                  }}
                  disabled={!userSession?.user?.referralCode}
                >
                  📋 Copy
                </button>
                <button 
                  className="copy-link-btn"
                  onClick={() => {
                    const referralLink = `https://intellegacy.app/register?ref=${userSession.user.referralCode}`;
                    if (navigator.share && userSession?.user?.referralCode) {
                      navigator.share({
                        title: 'Join me on Intellegacy!',
                        text: 'Check out this awesome platform!',
                        url: referralLink
                      });
                    } else if (userSession?.user?.referralCode) {
                      // Fallback to copying with message
                      navigator.clipboard.writeText(`Join me on Intellegacy! ${referralLink}`);
                      alert('Referral message copied to clipboard!');
                    }
                  }}
                  disabled={!userSession?.user?.referralCode}
                >
                  🔗 Share
                </button>
              </div>
            </div>
          </div>
      </div>
      
      <FomoAlert 
        isOpen={showFomoAlert}
        onClose={() => setShowFomoAlert(false)}
        featureName="Profile Management"
        customMessage="Probably want to complete registration first, then maybe you'll be allowed to edit your profile and upload pictures. Just saying... 🤷‍♂️"
      />

      <ImageCropModal
        isOpen={showCropModal}
        onClose={handleCropCancel}
        onCropComplete={handleCropComplete}
        imageSrc={cropImageSrc}
      />

      {showEmailVerification && (
        <ProfileEmailVerification
          currentEmail={originalEmail}
          newEmail={pendingEmail}
          onSuccess={handleEmailVerified}
          onCancel={() => setShowEmailVerification(false)}
        />
      )}
    </div>
  );
};

export default Profile;
